import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { RefreshCw, Timer, TimerOff, Check } from 'lucide-react'
import { DataTable } from '@/components/data/DataTable'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useIngressActive, useIngressHistory, useIngressSummary, ingressKeys } from '../hooks'
import { createActiveColumns, createHistoryColumns } from '../columns'
import type { IngressPeriod, IngressActiveItem, IngressHistoryItem, IngressSummaryItem } from '../api'

type RefreshOption = { label: string; value: number | false }

const REFRESH_OPTIONS: RefreshOption[] = [
    { label: '10s', value: 10000 },
    { label: '30s', value: 30000 },
    { label: '1m', value: 60000 },
    { label: 'Off', value: false },
]

export function IngressMonitoringPage() {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [historyPeriod, setHistoryPeriod] = useState<IngressPeriod>('day')
    const [autoRefresh, setAutoRefresh] = useState<number | false>(10000)
    const [showRefreshOptions, setShowRefreshOptions] = useState(false)
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date())
    const [isRefreshing, setIsRefreshing] = useState(false)

    const { data: activeData, isLoading: activeLoading, dataUpdatedAt } = useIngressActive(autoRefresh)
    const { data: historyData, isLoading: historyLoading } = useIngressHistory(historyPeriod)
    const { data: summaryData } = useIngressSummary(historyPeriod)

    // Track last refresh time
    useEffect(() => {
        if (dataUpdatedAt) setLastRefresh(new Date(dataUpdatedAt))
    }, [dataUpdatedAt])

    const activeList: IngressActiveItem[] = Array.isArray(activeData) ? activeData : []
    const historyList: IngressHistoryItem[] = Array.isArray(historyData) ? historyData : []
    const summaryList: IngressSummaryItem[] = Array.isArray(summaryData) ? summaryData : []

    const activeColumns = createActiveColumns(t)
    const historyColumns = createHistoryColumns(t)

    // Compute summary totals
    const totalObjects = summaryList.reduce((sum, s) => sum + (s.noObjects || 0), 0)
    const totalVersions = summaryList.reduce((sum, s) => sum + (s.noVersions || 0), 0)
    const totalFinished = summaryList.reduce((sum, s) => sum + (s.noFinished || 0), 0)
    const totalSizeMB = summaryList.reduce((sum, s) => sum + (s.sizeMB || 0), 0)

    const periodLabels: Record<IngressPeriod, string> = {
        day: t('ingressMonitoring.lastDay'),
        week: t('ingressMonitoring.lastWeek'),
        month: t('ingressMonitoring.lastMonth'),
    }

    // Manual refresh — invalidate all ingress queries
    async function handleRefresh() {
        setIsRefreshing(true)
        await queryClient.invalidateQueries({ queryKey: ingressKeys.all })
        setLastRefresh(new Date())
        setTimeout(() => setIsRefreshing(false), 600)
    }

    // Format the current auto-refresh label
    const currentRefreshLabel = autoRefresh === false
        ? 'Off'
        : REFRESH_OPTIONS.find(o => o.value === autoRefresh)?.label || `${(autoRefresh as number) / 1000}s`

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!showRefreshOptions) return
        const handler = () => setShowRefreshOptions(false)
        document.addEventListener('click', handler)
        return () => document.removeEventListener('click', handler)
    }, [showRefreshOptions])

    return (
        <div className="space-y-6">
            {/* Header with refresh controls */}
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-3xl font-bold">{t('ingressMonitoring.title')}</h1>
                    <p className="text-muted-foreground">{t('ingressMonitoring.description')}</p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Last updated indicator */}
                    <span className="text-xs text-muted-foreground">
                        Updated {lastRefresh.toLocaleTimeString()}
                    </span>

                    {/* Manual refresh button */}
                    <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
                        <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>

                    {/* Auto-refresh dropdown */}
                    <div className="relative">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); setShowRefreshOptions(!showRefreshOptions) }}
                            className={autoRefresh !== false ? 'border-green-300 dark:border-green-800' : ''}
                        >
                            {autoRefresh !== false
                                ? <Timer className="h-3.5 w-3.5 mr-1.5 text-green-600" />
                                : <TimerOff className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                            }
                            Auto: {currentRefreshLabel}
                        </Button>

                        {showRefreshOptions && (
                            <div className="absolute right-0 top-full mt-1 z-50 bg-popover border rounded-lg shadow-md py-1 min-w-[140px]"
                                onClick={(e) => e.stopPropagation()}>
                                <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                    Auto Refresh
                                </div>
                                {REFRESH_OPTIONS.map((opt) => (
                                    <button
                                        key={String(opt.value)}
                                        className={`w-full flex items-center justify-between px-3 py-1.5 text-sm hover:bg-accent transition-colors ${autoRefresh === opt.value ? 'text-primary font-medium' : ''}`}
                                        onClick={() => { setAutoRefresh(opt.value); setShowRefreshOptions(false) }}
                                    >
                                        <span className="flex items-center gap-2">
                                            {opt.value === false
                                                ? <TimerOff className="h-3.5 w-3.5 text-muted-foreground" />
                                                : <Timer className="h-3.5 w-3.5 text-green-600" />
                                            }
                                            {opt.value === false ? 'Off' : `Every ${opt.label}`}
                                        </span>
                                        {autoRefresh === opt.value && <Check className="h-3.5 w-3.5 text-primary" />}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            {t('ingressMonitoring.activeNow')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{activeList.length}</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            {t('ingressMonitoring.objectsIngressed')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{totalObjects}</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            {t('ingressMonitoring.versionsIngressed')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{totalVersions}</div>
                        <div className="text-xs text-muted-foreground">{totalFinished} {t('ingressMonitoring.finished')}</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            {t('ingressMonitoring.totalSize')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {totalSizeMB >= 1024 ? `${(totalSizeMB / 1024).toFixed(1)} GB` : `${totalSizeMB.toFixed(1)} MB`}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Active ingress table */}
            <div className="space-y-4">
                <h2 className="text-xl font-semibold">{t('ingressMonitoring.activeIngress')}</h2>
                {activeLoading ? (
                    <div className="flex items-center justify-center h-32">
                        <div className="text-muted-foreground">{t('common.loading')}</div>
                    </div>
                ) : activeList.length === 0 ? (
                    <Card>
                        <CardContent className="flex items-center justify-center h-32">
                            <p className="text-muted-foreground">{t('ingressMonitoring.noActiveIngress')}</p>
                        </CardContent>
                    </Card>
                ) : (
                    <DataTable
                        columns={activeColumns}
                        data={activeList}
                        searchKey="objectId"
                        searchPlaceholder={t('ingressMonitoring.searchPlaceholder')}
                    />
                )}
            </div>

            {/* History section with period filter */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold">{t('ingressMonitoring.completedIngress')}</h2>
                    <div className="flex gap-2">
                        {(['day', 'week', 'month'] as IngressPeriod[]).map((period) => (
                            <Button
                                key={period}
                                variant={historyPeriod === period ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setHistoryPeriod(period)}
                            >
                                {periodLabels[period]}
                            </Button>
                        ))}
                    </div>
                </div>
                {historyLoading ? (
                    <div className="flex items-center justify-center h-32">
                        <div className="text-muted-foreground">{t('common.loading')}</div>
                    </div>
                ) : historyList.length === 0 ? (
                    <Card>
                        <CardContent className="flex items-center justify-center h-32">
                            <p className="text-muted-foreground">{t('ingressMonitoring.noHistory')}</p>
                        </CardContent>
                    </Card>
                ) : (
                    <DataTable
                        columns={historyColumns}
                        data={historyList}
                        searchKey="objectId"
                        searchPlaceholder={t('ingressMonitoring.searchPlaceholder')}
                    />
                )}
            </div>
        </div>
    )
}
